import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, reportsAPI } from '../services/api';
import toast from 'react-hot-toast';
import AutoPromptsTab from '../components/admin/AutoPromptsTab';
import ClaimsTab from '../components/admin/ClaimsTab';
import PromptsTab from '../components/admin/PromptsTab';
import ReportsTab from '../components/admin/ReportsTab';
import { Card } from '../components/ui';

/**
 * Admin Dashboard - Refactored and componentized
 * Split into separate tab components for better maintainability
 */
const Admin = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('auto-prompts');

  // State for each tab
  const [claims, setClaims] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [reportedPrompts, setReportedPrompts] = useState([]);
  const [autoPrompts, setAutoPrompts] = useState([]);

  // Auto-prompts filters and pagination
  const [apFilters, setApFilters] = useState({
    status: 'all',
    hidePublished: true,
    minQuality: '',
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });
  const [apPage, setApPage] = useState(0);
  const [apLimit, setApLimit] = useState(50);
  const [apTotal, setApTotal] = useState(0);
  const [apDetails, setApDetails] = useState(null);
  const [apSelection, setApSelection] = useState({});

  // Loading states
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingAutoPrompts, setLoadingAutoPrompts] = useState(false);

  const isAdmin = userProfile && ['admin', 'system', 'moderator'].includes(userProfile.role);

  // Initial data fetch
  useEffect(() => {
    if (!isAdmin) return;

    Promise.all([
      fetchClaims(),
      fetchPrompts(),
      fetchReportedPrompts(),
      fetchAutoPrompts(),
    ]).catch((error) => {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load some admin data');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Fetch functions
  const fetchClaims = async () => {
    setLoadingClaims(true);
    try {
      const res = await adminAPI.getPendingClaims();
      setClaims(res.data.claims || []);
    } catch (err) {
      console.error('Failed to load claims', err);
      toast.error('Failed to load claims');
    } finally {
      setLoadingClaims(false);
    }
  };

  const fetchPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const res = await adminAPI.getPendingPrompts({ limit: 30 });
      setPrompts(res.data.prompts || []);
    } catch (err) {
      console.error('Failed to load prompts', err);
      toast.error('Failed to load prompts');
    } finally {
      setLoadingPrompts(false);
    }
  };

  const fetchReportedPrompts = async () => {
    setLoadingReports(true);
    try {
      const res = await reportsAPI.getAllReported({ limit: 50 });
      setReportedPrompts(res.data.prompts || []);
    } catch (err) {
      console.error('Failed to load reported prompts', err);
      toast.error('Failed to load reported prompts');
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchAutoPrompts = useCallback(async () => {
    setLoadingAutoPrompts(true);
    try {
      const params = {
        limit: apLimit,
        offset: apPage * apLimit,
        status: apFilters.status !== 'all' ? apFilters.status : undefined,
        hide_published: apFilters.hidePublished,
        minQuality: apFilters.minQuality || undefined,
        search: apFilters.search || undefined,
        sortBy: apFilters.sortBy,
        sortOrder: apFilters.sortOrder,
      };
      const res = await adminAPI.getAutoPrompts(params);
      setAutoPrompts(res.data.autoPrompts || []);
      setApTotal(res.data.total || 0);
      setApSelection({});
    } catch (err) {
      console.error('Failed to load auto prompts', err);
      toast.error('Failed to load auto prompts');
    } finally {
      setLoadingAutoPrompts(false);
    }
  }, [apFilters, apLimit, apPage]);

  // Refetch auto-prompts when filters change
  useEffect(() => {
    if (activeTab === 'auto-prompts' && isAdmin) {
      fetchAutoPrompts();
    }
  }, [activeTab, isAdmin, fetchAutoPrompts]);

  // Action handlers
  const approveClaim = async (id) => {
    try {
      await adminAPI.approveClaim(id);
      toast.success('Claim approved');
      await fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    }
  };

  const rejectClaim = async (id) => {
    try {
      await adminAPI.rejectClaim(id);
      toast.success('Claim rejected');
      await fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed');
    }
  };

  const approvePrompt = async (id) => {
    try {
      await adminAPI.updatePromptStatus(id, {
        status: 'published',
        moderation_status: 'approved',
        moderation_notes: 'Approved by admin',
      });
      toast.success('Prompt approved');
      await fetchPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    }
  };

  const rejectPrompt = async (id) => {
    try {
      await adminAPI.updatePromptStatus(id, {
        status: 'rejected',
        moderation_status: 'rejected',
        moderation_notes: 'Rejected by admin',
      });
      toast.success('Prompt rejected');
      await fetchPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed');
    }
  };

  const dismissReports = async (promptId) => {
    if (
      !window.confirm(
        'Dismiss all reports for this prompt? The prompt will be restored to normal visibility.'
      )
    ) {
      return;
    }
    try {
      await reportsAPI.dismissReports(promptId);
      toast.success('Reports dismissed');
      await fetchReportedPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to dismiss reports');
    }
  };

  const banPrompt = async (promptId) => {
    if (!window.confirm('Ban this prompt permanently? This action cannot be undone.')) {
      return;
    }
    try {
      await reportsAPI.banPrompt(promptId);
      toast.success('Prompt banned');
      await fetchReportedPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to ban prompt');
    }
  };

  const publishAutoPrompt = async (id) => {
    try {
      await adminAPI.publishAutoPrompt(id);
      toast.success('Auto prompt published!');
      await fetchAutoPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Publish failed');
    }
  };

  const deleteAutoPrompt = async (id) => {
    if (!window.confirm('Delete this auto prompt? This action cannot be undone.')) {
      return;
    }
    try {
      await adminAPI.deleteAutoPrompt(id);
      toast.success('Auto prompt deleted');
      await fetchAutoPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const bulkPublish = async () => {
    const ids = Object.entries(apSelection)
      .filter(([, sel]) => sel)
      .map(([id]) => id);
    if (ids.length === 0) return toast('No prompts selected');
    try {
      const res = await adminAPI.bulkPublishAutoPrompts(ids);
      toast.success(res.data?.message || 'Bulk publish complete');
      await fetchAutoPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk publish failed');
    }
  };

  const bulkDelete = async () => {
    const ids = Object.entries(apSelection)
      .filter(([, sel]) => sel)
      .map(([id]) => id);
    if (ids.length === 0) return toast('No prompts selected');
    if (!window.confirm(`Delete ${ids.length} auto prompts? This cannot be undone.`)) return;
    try {
      const res = await adminAPI.bulkDeleteAutoPrompts(ids);
      toast.success(res.data?.message || 'Bulk delete complete');
      await fetchAutoPrompts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk delete failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card>
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            You don't have permission to access the admin dashboard.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 dark:text-white">Admin Dashboard</h1>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-spooky-bg-medium rounded-lg shadow-md p-1 inline-flex mb-6 gap-1">
        <button
          onClick={() => setActiveTab('auto-prompts')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeTab === 'auto-prompts'
              ? 'bg-boop-primary text-white'
              : 'text-gray-600 dark:text-gray-300 hover:text-boop-primary dark:hover:text-boop-primary'
          }`}
        >
          Auto Prompts ({autoPrompts.length})
        </button>
        <button
          onClick={() => setActiveTab('claims')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeTab === 'claims'
              ? 'bg-boop-primary text-white'
              : 'text-gray-600 dark:text-gray-300 hover:text-boop-primary dark:hover:text-boop-primary'
          }`}
        >
          Claims ({claims.length})
        </button>
        <button
          onClick={() => setActiveTab('prompts')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeTab === 'prompts'
              ? 'bg-boop-primary text-white'
              : 'text-gray-600 dark:text-gray-300 hover:text-boop-primary dark:hover:text-boop-primary'
          }`}
        >
          Prompts ({prompts.length})
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`px-6 py-2 rounded-lg font-semibold transition ${
            activeTab === 'reports'
              ? 'bg-boop-primary text-white'
              : 'text-gray-600 dark:text-gray-300 hover:text-boop-primary dark:hover:text-boop-primary'
          }`}
        >
          Reports ({reportedPrompts.length})
        </button>
      </div>

      {/* Tab Content */}
      <Card padding="comfortable">
        {activeTab === 'auto-prompts' && (
          <AutoPromptsTab
            autoPrompts={autoPrompts}
            loading={loadingAutoPrompts}
            filters={apFilters}
            setFilters={setApFilters}
            page={apPage}
            setPage={setApPage}
            limit={apLimit}
            setLimit={setApLimit}
            total={apTotal}
            selection={apSelection}
            setSelection={setApSelection}
            details={apDetails}
            setDetails={setApDetails}
            onPublish={publishAutoPrompt}
            onDelete={deleteAutoPrompt}
            onApproveClaim={approveClaim}
            onBulkPublish={bulkPublish}
            onBulkDelete={bulkDelete}
            onRefresh={fetchAutoPrompts}
          />
        )}

        {activeTab === 'claims' && (
          <ClaimsTab
            claims={claims}
            loading={loadingClaims}
            onApprove={approveClaim}
            onReject={rejectClaim}
          />
        )}

        {activeTab === 'prompts' && (
          <PromptsTab
            prompts={prompts}
            loading={loadingPrompts}
            onApprove={approvePrompt}
            onReject={rejectPrompt}
          />
        )}

        {activeTab === 'reports' && (
          <ReportsTab
            reportedPrompts={reportedPrompts}
            loading={loadingReports}
            onDismiss={dismissReports}
            onBan={banPrompt}
          />
        )}
      </Card>
    </div>
  );
};

export default Admin;
